import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { FaRocket, FaCode, FaLaptopCode, FaHeart } from 'react-icons/fa';

const About = () => {
  const teamMembers = [
    {
      name: "Ankit Maharshi",
      role: "Founder & Lead Developer",
      image: "https://i.pravatar.cc/300?img=68",
      bio: "10+ years in web development, specializing in React and Node.js.",
      contact: {
        email: "ankitmaharshi55@gmail.com",
        phone: "+91-9468532536"
      }
    },
    {
      name: "Trinity Jones",
      role: "UI/UX Designer",
      image: "https://i.pravatar.cc/300?img=48",
      bio: "Award-winning designer with expertise in futuristic UI trends."
    },
    {
      name: "Morpheus Jackson",
      role: "Backend Engineer",
      image: "https://i.pravatar.cc/300?img=67",
      bio: "Cloud infrastructure specialist with focus on scalable solutions."
    },
    {
      name: "Oracle Chen",
      role: "Project Manager",
      image: "https://i.pravatar.cc/300?img=47",
      bio: "Certified Scrum master with track record of successful deliveries."
    }
  ];

  const timeline = [
    {
      year: "2019",
      title: "Company Founded",
      description: "CyberNest began with just two developers in a small garage."
    },
    {
      year: "2020",
      title: "First Major Client",
      description: "Secured our first enterprise client and expanded the team."
    },
    {
      year: "2021",
      title: "New Office",
      description: "Moved to a dedicated office space and grew to 10 team members."
    },
    {
      year: "2022",
      title: "International Expansion",
      description: "Started serving clients across Europe and Southeast Asia."
    },
    {
      year: "2023",
      title: "Technology Awards",
      description: "Won 'Most Innovative Web Agency' at TechFest 2023."
    }
  ];

  return (
    <div className="min-h-screen pt-28 pb-20 bg-cyber-dark">
      <div className="container mx-auto px-4">
        {/* Hero Section */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <h1 className="text-5xl font-orbitron font-bold mb-6">
            <span className="text-cyber-pink">About</span> Us
          </h1>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto">
            We are a team of creative developers, designers, and tech enthusiasts passionate about
            creating futuristic digital experiences.
          </p>
        </motion.div>
        
        {/* Mission & Vision */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-20">
          <motion.div 
            initial={{ opacity: 0, x: -30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="cyber-card p-6"
          >
            <h2 className="text-2xl font-orbitron font-bold mb-4 text-cyber-neon">Our Mission</h2>
            <p className="text-gray-300">
              To revolutionize the web development industry by creating cutting-edge websites that blend 
              aesthetics with functionality. We strive to push the boundaries of what's possible in web design, 
              delivering futuristic digital experiences that help our clients stand out in the digital landscape.
            </p>
          </motion.div>
          
          <motion.div 
            initial={{ opacity: 0, x: 30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="cyber-card p-6"
          >
            <h2 className="text-2xl font-orbitron font-bold mb-4 text-cyber-pink">Our Vision</h2>
            <p className="text-gray-300">
              To become the go-to agency for businesses seeking innovative web solutions. We envision 
              a digital world where websites are not just functional tools but immersive experiences 
              that captivate users and drive business growth.
            </p>
          </motion.div>
        </div>
        
        {/* What Makes Us Different */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.6 }}
          className="mb-20"
        >
          <h2 className="text-3xl font-orbitron font-bold mb-10 text-center">What Makes Us <span className="text-cyber-neon">Different</span></h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="cyber-card p-6 text-center hover:transform hover:scale-105 transition-all duration-300">
              <div className="bg-cyber-pink/20 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <FaRocket className="text-cyber-pink text-2xl" />
              </div>
              <h3 className="text-xl font-orbitron font-bold mb-2 text-white">Cutting-Edge</h3>
              <p className="text-gray-300">We use the latest technologies and design trends to create modern websites.</p>
            </div>
            
            <div className="cyber-card p-6 text-center hover:transform hover:scale-105 transition-all duration-300">
              <div className="bg-cyber-neon/20 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <FaCode className="text-cyber-neon text-2xl" />
              </div>
              <h3 className="text-xl font-orbitron font-bold mb-2 text-white">Clean Code</h3>
              <p className="text-gray-300">Our developers write efficient, maintainable code that's built to last.</p>
            </div>
            
            <div className="cyber-card p-6 text-center hover:transform hover:scale-105 transition-all duration-300">
              <div className="bg-cyber-purple/20 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <FaLaptopCode className="text-cyber-purple text-2xl" />
              </div>
              <h3 className="text-xl font-orbitron font-bold mb-2 text-white">Responsive Design</h3>
              <p className="text-gray-300">Our websites look and perform beautifully on any device and screen size.</p>
            </div>
            
            <div className="cyber-card p-6 text-center hover:transform hover:scale-105 transition-all duration-300">
              <div className="bg-cyber-pink/20 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <FaHeart className="text-cyber-pink text-2xl" />
              </div>
              <h3 className="text-xl font-orbitron font-bold mb-2 text-white">Client-Focused</h3>
              <p className="text-gray-300">We prioritize client satisfaction and long-term relationships above all.</p>
            </div>
          </div>
        </motion.div>
        
        {/* Our Team */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.8 }}
          className="mb-20"
        >
          <h2 className="text-3xl font-orbitron font-bold mb-10 text-center">Meet Our <span className="text-cyber-pink">Team</span></h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {teamMembers.map((member, index) => (
              <div 
                key={index} 
                className="cyber-card p-1 hover:shadow-cyber-pink/30 hover:shadow-lg transition-all duration-300"
              >
                <div className="relative overflow-hidden">
                  <img 
                    src={member.image} 
                    alt={member.name} 
                    className="w-full object-cover h-64 filter grayscale-[30%]"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-cyber-dark via-transparent to-transparent"></div>
                  <div className="absolute inset-0 bg-cyber-pink/10 opacity-0 hover:opacity-100 transition-opacity duration-300"></div>
                </div>
                <div className="p-6">
                  <h3 className="text-xl font-orbitron font-bold text-cyber-neon">{member.name}</h3>
                  <p className="text-cyber-pink text-sm mb-2">{member.role}</p>
                  <p className="text-gray-300 text-sm mb-3">{member.bio}</p>
                  {member.contact && (
                    <div className="text-xs text-gray-400 space-y-1">
                      {member.contact.email && (
                        <p>Email: {member.contact.email}</p>
                      )}
                      {member.contact.phone && (
                        <p>Phone: {member.contact.phone}</p>
                      )}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </motion.div>
        
        {/* Company Timeline */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 1 }}
        >
          <h2 className="text-3xl font-orbitron font-bold mb-10 text-center">Our <span className="text-cyber-neon">Journey</span></h2>
          
          <div className="relative">
            {/* Timeline center line */}
            <div className="absolute left-1/2 transform -translate-x-1/2 h-full w-1 bg-cyber-purple"></div>
            
            {/* Timeline items */}
            <div className="space-y-16">
              {timeline.map((item, index) => (
                <div 
                  key={index} 
                  className={`relative flex ${index % 2 === 0 ? 'justify-start md:justify-end' : 'justify-start'} md:${index % 2 === 0 ? 'text-right' : 'text-left'}`}
                >
                  <div className={`cyber-card p-6 w-full md:w-5/12 ${index % 2 === 0 ? 'md:mr-8' : 'md:ml-8'}`}>
                    <span className="font-orbitron text-cyber-pink text-sm">{item.year}</span>
                    <h3 className="text-xl font-orbitron text-white mt-2">{item.title}</h3>
                    <p className="text-gray-300 mt-2">{item.description}</p>
                  </div>
                    
                  {/* Timeline dot */}
                  <div className="absolute left-1/2 transform -translate-x-1/2 w-4 h-4 rounded-full bg-cyber-neon border-4 border-cyber-dark"></div>
                </div>
              ))}
            </div>
          </div>
        </motion.div>

        {/* Contact CTA */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 1.2 }}
          className="mt-24 text-center"
        >
          <div className="cyber-card p-8 md:p-12">
            <h2 className="text-2xl md:text-3xl font-orbitron font-bold mb-4">
              Ready to work with <span className="text-cyber-pink">CyberNest</span>?
            </h2>
            <p className="text-gray-300 mb-8 max-w-2xl mx-auto">
              Contact us today to discuss your project needs and transform your web presence.
            </p>
            <div className="mb-8 space-y-2 text-gray-300">
              <p>Email: <span className="text-cyber-neon">ankitmaharshi55@gmail.com</span></p>
              <p>Phone: <span className="text-cyber-neon">+91-9468532536</span></p>
            </div>
            <Link to="/contact" className="cyber-button">
              <span>Get In Touch</span>
            </Link>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default About;